import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Layout from "../../components/layout";
import {Field, FieldsManager} from "../../data/services/fields.js";
import {login, resetUserMessage} from "../../data/actions/user";
import LoaderSmall from "../../components/loader-small";
import InfoParagraph from "../../components/info-paragraph";
import {Link} from "react-router-dom";
import {EyeIcon, EyeOffIcon, LockClosedIcon, UserIcon} from "@heroicons/react/outline";
import {getProp} from "../../common/util/util-helpers";

const LoginView = (props) => {

    /** Constants
     ================================================================= */
    const {history, translate} = props

    const dispatch = useDispatch();

    const user = useSelector((state) => state.user)

    const [accessToken, setAccessToken] = useState(null);

    /** Fields/Data definitions
     ================================================================= */
    const getFields = () => ({
        username: new Field('username', '', ['empty', 'email'], false, 'email'),
        password: new Field('password', '', ['empty'], false, 'password')
    })

    const [fields, setFields] = useState(getFields())
    const [showPassword, setShowPassword] = useState(false)

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        dispatch(resetUserMessage())
    }, [])

    useEffect(() => {
        if (!!user.data) {
            history.push("/");
        }
    }, [user]);

    useEffect(() => {
        if (!!accessToken) {
            dispatch(login({
                AzureAD: true,
                accessToken: accessToken
            }));
        }
    }, [accessToken]);

    /** Data events
     ================================================================= */
    const onKeyDown = (event) => {
        if (event.key === 'Enter') {
            onSubmit(event);
        }
    };

    const onSubmit = (event) => {
        event && event.preventDefault();

        const validatedFields = FieldsManager.validateFields(fields);

        if (FieldsManager.checkFieldsForErrors(validatedFields)) {
            dispatch(login(FieldsManager.getFieldKeyValues(validatedFields)));
        } else {
            setFields(validatedFields);
        }
    }

    /** UI events
     ================================================================= */
    const handleInputChange = (name, value) => {
        setFields(FieldsManager.updateField(fields, name, value))
    }

    /** Render
     ================================================================= */
    const inputWrapperClassName =
        "relative flex items-center w-full h-[58px] rounded-[10px] border border-[#dfe3ec] bg-white px-6 transition focus-within:border-[#1f7bea] focus-within:ring-2 focus-within:ring-blue-100";

    const inputIconClassName =
        "mr-5 h-6 w-6 text-[#6f7685] flex-shrink-0";

    const inputClassName =
        "w-full h-full border-none bg-transparent text-[18px] font-medium text-[#1f2937] placeholder:text-[#a7adbb] placeholder:font-medium outline-none";

    const passwordEyeIconClassName =
        "ml-4 h-6 w-6 cursor-pointer text-[#6f7685] flex-shrink-0 hover:text-[#1f2937]";

    return (
        <Layout user={user} history={history}>
            <section className="gradient-form h-[100vh] w-full bg-neutral-200 dark:bg-neutral-700">
                <div className="container h-full m-auto w-full p-10">
                    <div
                        className="g-6 flex h-full flex-wrap items-center justify-center text-neutral-800 dark:text-neutral-200">
                        <div className="w-full xl:w-1/2">
                            <div
                                className="block rounded-lg bg-white shadow-lg dark:bg-neutral-800">
                                <div className="">
                                    <div className="px-4 md:px-0">
                                        <div className="md:mx-6 md:p-12">
                                            <div className="text-center mb-2">
                                                <img
                                                    className="mx-auto w-sm"
                                                    src="/images/nursing_temp_logo.png"
                                                    alt="logo"/>
                                            </div>

                                            <form onKeyPress={onKeyDown}>
                                                <h1 className="mb-4 text-center font-bold text-xl">Please login to your account</h1>

                                                <div className={inputWrapperClassName + (" pr-0 mb-4")}>
                                                    <UserIcon className={inputIconClassName} />
                                                    <input
                                                        className={inputClassName + (" rounded-r-lg")}
                                                        type="text"
                                                        placeholder="Email or Username"
                                                        onChange={(e) => {
                                                            handleInputChange("username", e.target.value)
                                                        }}
                                                        value={getProp(fields, 'username.value', '')}
                                                    />
                                                </div>

                                                <div className={inputWrapperClassName}>
                                                    <LockClosedIcon className={inputIconClassName} />
                                                    <input
                                                        className={inputClassName}
                                                        type={showPassword ? "text" : "password"}
                                                        placeholder="Password"
                                                        onChange={(e) => {
                                                            handleInputChange("password", e.target.value)
                                                        }}
                                                        value={getProp(fields, 'password.value', '')}
                                                    />
                                                    {!!showPassword ? (
                                                        <EyeOffIcon
                                                            onClick={() => {
                                                                setShowPassword(!showPassword)
                                                            }}
                                                            className={passwordEyeIconClassName}
                                                        />
                                                    ) : (
                                                        <EyeIcon
                                                            onClick={() => {
                                                                setShowPassword(!showPassword)
                                                            }}
                                                            className={passwordEyeIconClassName}
                                                        />
                                                    )}
                                                </div>

                                                {user.isLoading && (
                                                    <div className="flex justify-center">
                                                        <LoaderSmall/>
                                                    </div>
                                                )}

                                                {!user.isLoading && (
                                                    <div>
                                                        <div className="mb-12 pb-1 pt-4 text-right">
                                                            <div className={'mb-4 text-primary-400 font-semibold'}>
                                                                <Link to="/reset-password">Forgot password?</Link>
                                                            </div>

                                                            <button
                                                                className="rounded-lg bg-primary-400 mb-3 inline-block w-full rounded px-6 py-4 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_rgba(0,0,0,0.2)] transition duration-150 ease-in-out hover:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)] focus:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)] focus:outline-none focus:ring-0 active:shadow-[0_8px_9px_-4px_rgba(0,0,0,0.1),0_4px_18px_0_rgba(0,0,0,0.2)]"
                                                                type="button"
                                                                onClick={onSubmit}
                                                            >
                                                                Login
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                                {user.errorMessage && (
                                                    <InfoParagraph
                                                        type="danger"
                                                        className={'mt-5'}
                                                    >
                                                        {translate(user.errorMessage)}
                                                    </InfoParagraph>
                                                )}
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default LoginView;
